/**
 * offlineQueue.js — tiny IndexedDB-backed queue for events that need to
 * be persisted to the backend but might fail because the device is
 * offline (no signal at a customer site, plane mode, etc.).
 *
 * Usage:
 *   import { enqueue, flushQueue, queueCount, onQueueChange } from '@/utils/offlineQueue';
 *
 *   try {
 *     await api.post('/attendance/punch', payload);
 *   } catch (e) {
 *     if (isNetworkError(e)) {
 *       await enqueue('attendance.punch', payload);
 *       // pretend to succeed — the queue will retry in the background
 *     } else {
 *       throw e;   // real validation error — surface it
 *     }
 *   }
 *
 * The queue auto-flushes on `online` and `focus` events. Each queued
 * record is replayed by POSTing to its configured endpoint. Successful
 * sends are deleted; failures are retried on the next trigger (the row
 * keeps a `try_count` for diagnostics).
 *
 * Currently registered job kinds (extend the `JOB_HANDLERS` map below):
 *   • 'attendance.punch'      → POST /attendance/punch
 *
 * The store stays under 1 MB even with thousands of rows (each event is
 * <200 bytes), so we don't need any expiry / pruning logic for now.
 */
import api from './api';

const DB_NAME = 'zartec_offline';
const STORE = 'jobs';
const VERSION = 1;
let dbPromise = null;

const listeners = new Set();
function notify() {
  queueCount().then((n) => {
    listeners.forEach((cb) => {
      try { cb(n); } catch { /* swallow */ }
    });
  });
}

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB not supported in this browser'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
  return dbPromise;
}

async function withStore(mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const result = fn(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

export async function enqueue(kind, payload) {
  try {
    await withStore('readwrite', (store) => {
      store.add({ kind, payload, queued_at: Date.now(), try_count: 0 });
    });
    notify();
  } catch (e) {
    // IndexedDB unavailable — best we can do is warn. The caller will
    // already have seen a network error; nothing more to surface.
    console.warn('[offlineQueue] enqueue failed:', e);
  }
}

export async function queueCount() {
  try {
    return await new Promise((resolve, reject) => {
      withStore('readonly', (store) => {
        const req = store.count();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
      });
    });
  } catch {
    return 0;
  }
}

async function listAll() {
  return await new Promise((resolve, reject) => {
    withStore('readonly', (store) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  });
}

async function remove(id) {
  await withStore('readwrite', (store) => store.delete(id));
}

async function bumpTryCount(id, n) {
  await withStore('readwrite', (store) => {
    const req = store.get(id);
    req.onsuccess = () => {
      const row = req.result;
      if (row) {
        row.try_count = n;
        store.put(row);
      }
    };
  });
}

/**
 * Map each job `kind` to the function that re-sends it.
 * Extend this when adding new offline-capable endpoints.
 */
const JOB_HANDLERS = {
  'attendance.punch': (payload) => api.post('/attendance/punch', payload),
};

let flushInFlight = false;
export async function flushQueue() {
  if (flushInFlight) return;     // dedupe overlapping calls
  if (!navigator.onLine) return; // nothing to do offline
  flushInFlight = true;
  try {
    const rows = await listAll();
    for (const row of rows) {
      const handler = JOB_HANDLERS[row.kind];
      if (!handler) {
        // Unknown kind (stale schema?) — drop it so the queue can drain.
        await remove(row.id);
        continue;
      }
      try {
        await handler(row.payload);
        await remove(row.id);
      } catch (e) {
        // Only retry on network errors. Server errors (4xx other than
        // 408/429) mean the payload is wrong and will never succeed —
        // drop them so the queue doesn't get stuck.
        const status = e?.response?.status;
        const isNetwork = !status || status === 408 || status === 429 || status === 502 || status === 503 || status === 504;
        if (isNetwork) {
          await bumpTryCount(row.id, (row.try_count || 0) + 1);
          break; // bail this flush — try again later
        } else {
          console.warn('[offlineQueue] dropping job with permanent error:', e?.response?.data, row);
          await remove(row.id);
        }
      }
    }
  } finally {
    flushInFlight = false;
    notify();
  }
}

export function onQueueChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function isNetworkError(e) {
  // Axios: no response object → network failure or CORS preflight death.
  return !e?.response;
}

// Auto-flush triggers — wire once at module load. Safe to import from
// multiple places; the listeners are idempotent for our purposes.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => { flushQueue(); });
  window.addEventListener('focus', () => { flushQueue(); });
  // Best-effort initial flush in case the app starts up online but the
  // queue contains leftovers from the previous session.
  setTimeout(() => { flushQueue(); }, 1500);
}
